import React, { useState, useEffect } from 'react';
import { Users, Plus, Trash2, Shield, Mail, Crown, UserCheck, AlertCircle, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamSettings = () => {
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [newTeamName, setNewTeamName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('developer');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => { fetchTeams(); }, []);

  const fetchTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/teams`, { credentials: 'include' });
      const data = await res.json();
      setTeams(data.teams || []);
      if (data.teams?.length > 0 && !selectedTeam) {
        setSelectedTeam(data.teams[0]);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createTeam = async () => {
    if (!newTeamName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/teams`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTeamName }),
      });
      const data = await res.json();
      if (res.ok) {
        setNewTeamName('');
        setMessage({ type: 'success', text: 'Team created' });
        fetchTeams();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed' });
      }
    } catch (e) { setMessage({ type: 'error', text: 'Network error' }); }
  };

  const inviteMember = async () => {
    if (!inviteEmail.trim() || !selectedTeam) return;
    try {
      const res = await fetch(`${API_URL}/api/teams/${selectedTeam.id}/invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      const data = await res.json();
      if (res.ok) {
        setInviteEmail('');
        setMessage({ type: 'success', text: data.message });
        fetchTeams();
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed' });
      }
    } catch (e) { setMessage({ type: 'error', text: 'Network error' }); }
  };

  const removeMember = async (userId) => {
    if (!selectedTeam) return;
    try {
      await fetch(`${API_URL}/api/teams/${selectedTeam.id}/members/${userId}`, {
        method: 'DELETE', credentials: 'include',
      });
      setMessage({ type: 'success', text: 'Member removed' });
      fetchTeams();
    } catch (e) { setMessage({ type: 'error', text: 'Failed to remove' }); }
  };

  const updateRole = async (userId, newRole) => {
    if (!selectedTeam) return;
    try {
      await fetch(`${API_URL}/api/teams/${selectedTeam.id}/members/${userId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      setMessage({ type: 'success', text: 'Role updated' });
      fetchTeams();
    } catch (e) { setMessage({ type: 'error', text: 'Failed to update' }); }
  };

  const roleIcon = (role) => {
    if (role === 'owner') return <Crown size={14} style={{ color: '#f59e0b' }} />;
    if (role === 'admin') return <Shield size={14} style={{ color: '#8b5cf6' }} />;
    return <UserCheck size={14} style={{ color: '#6b7280' }} />;
  };

  if (loading) return <div style={{ padding: '1rem', color: 'hsl(var(--muted-foreground))' }}>Loading...</div>;

  return (
    <div data-testid="team-settings">
      {message && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
          background: message.type === 'success' ? 'hsl(142 70% 15%)' : 'hsl(0 70% 15%)',
          color: message.type === 'success' ? 'hsl(142 70% 70%)' : 'hsl(0 70% 70%)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <AlertCircle size={14} /> {message.text}
          <button onClick={() => setMessage(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {teams.length === 0 ? (
        <div style={{ border: '1px dashed hsl(var(--border))', borderRadius: '0.5rem', padding: '1.5rem', textAlign: 'center' }}>
          <Users size={28} style={{ margin: '0 auto 0.5rem', color: 'hsl(var(--muted-foreground))', opacity: 0.5 }} />
          <p style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>Create your team</p>
          <div style={{ display: 'flex', gap: '0.5rem', maxWidth: '300px', margin: '0 auto' }}>
            <input
              data-testid="team-name-input"
              value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
              placeholder="Team name" onKeyDown={e => e.key === 'Enter' && createTeam()}
              style={{ flex: 1, padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', fontSize: '0.8125rem' }}
            />
            <button data-testid="create-team-btn" onClick={createTeam} style={{
              padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
              background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))',
            }}><Plus size={14} /></button>
          </div>
        </div>
      ) : (
        <>
          {/* Team selector */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600 }}>{selectedTeam?.name || 'Select team'}</h3>
            <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>{selectedTeam?.members?.length || 0} members</span>
          </div>

          {/* Members list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginBottom: '0.75rem' }}>
            {selectedTeam?.members?.map((m, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem',
                background: 'hsl(var(--secondary))', borderRadius: '0.375rem', fontSize: '0.8125rem',
              }}>
                {roleIcon(m.role)}
                <span style={{ flex: 1 }}>{m.email}</span>
                <select
                  value={m.role} disabled={m.role === 'owner'}
                  onChange={e => updateRole(m.user_id, e.target.value)}
                  style={{ background: 'transparent', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem', fontSize: '0.75rem', padding: '0.125rem 0.25rem', color: 'hsl(var(--foreground))' }}
                >
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="developer">Developer</option>
                </select>
                {m.role !== 'owner' && (
                  <button onClick={() => removeMember(m.user_id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0 70% 60%)', padding: '0.125rem',
                  }}><Trash2 size={13} /></button>
                )}
              </div>
            ))}
          </div>

          {/* Invite form */}
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Mail size={14} style={{ position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)', color: 'hsl(var(--muted-foreground))' }} />
              <input
                data-testid="invite-email-input"
                value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
                placeholder="Invite by email" onKeyDown={e => e.key === 'Enter' && inviteMember()}
                style={{ width: '100%', padding: '0.5rem 0.5rem 0.5rem 2rem', borderRadius: '0.375rem', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', fontSize: '0.8125rem' }}
              />
            </div>
            <select
              data-testid="invite-role-select"
              value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', fontSize: '0.8125rem' }}
            >
              <option value="developer">Developer</option>
              <option value="admin">Admin</option>
            </select>
            <button data-testid="invite-btn" onClick={inviteMember} style={{
              padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
              background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))',
            }}>Invite</button>
          </div>
        </>
      )}
    </div>
  );
};

export default TeamSettings;
